<template>
  <div class="period-selector">
    <SelectButton
      :modelValue="periodType"
      :options="periodTypeOptions"
      optionLabel="label"
      optionValue="value"
      :allowEmpty="false"
      @update:modelValue="$emit('update:periodType', $event)"
    />

    <div class="period-controls">
      <Select
        v-if="periodType === 'month'"
        :modelValue="selectedMonth"
        :options="monthOptions"
        optionLabel="label"
        optionValue="value"
        class="period-dropdown"
        @update:modelValue="$emit('update:selectedMonth', $event)"
      />

      <Select
        v-else-if="periodType === 'quarter'"
        :modelValue="selectedQuarter"
        :options="quarterOptions"
        optionLabel="label"
        optionValue="value"
        class="period-dropdown"
        @update:modelValue="$emit('update:selectedQuarter', $event)"
      />

      <Select
        v-else-if="periodType === 'year'"
        :modelValue="selectedYear"
        :options="yearOptions"
        optionLabel="label"
        optionValue="value"
        class="period-dropdown"
        @update:modelValue="$emit('update:selectedYear', $event)"
      />

      <div v-else class="custom-range">
        <DatePicker
          :modelValue="customStartDate"
          date-format="dd/mm/yy"
          :maxDate="customEndDate ?? undefined"
          class="custom-date"
          @update:modelValue="onCustomStartDateChange"
        />
        <span class="range-separator">–</span>
        <DatePicker
          :modelValue="customEndDate"
          date-format="dd/mm/yy"
          :minDate="customStartDate ?? undefined"
          class="custom-date"
          @update:modelValue="onCustomEndDateChange"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  AdminPeriodType,
  MonthOption,
  QuarterOption,
  YearOption
} from '@/composables/useAdminPeriod'
import DatePicker from 'primevue/datepicker'
import Select from 'primevue/select'
import SelectButton from 'primevue/selectbutton'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

defineProps<{
  periodType: AdminPeriodType
  monthOptions: MonthOption[]
  quarterOptions: QuarterOption[]
  yearOptions: YearOption[]
  selectedMonth: string
  selectedQuarter: string
  selectedYear: number
  customStartDate: Date | null
  customEndDate: Date | null
}>()

const emit = defineEmits<{
  'update:periodType': [value: AdminPeriodType]
  'update:selectedMonth': [value: string]
  'update:selectedQuarter': [value: string]
  'update:selectedYear': [value: number]
  'update:customStartDate': [value: Date | null]
  'update:customEndDate': [value: Date | null]
}>()

function normalizeDate(value: Date | Date[] | (Date | null)[] | null | undefined): Date | null {
  if (value instanceof Date) {
    return value
  }
  if (Array.isArray(value) && value[0] instanceof Date) {
    return value[0]
  }
  return null
}

function onCustomStartDateChange(value: Date | Date[] | (Date | null)[] | null | undefined): void {
  emit('update:customStartDate', normalizeDate(value))
}

function onCustomEndDateChange(value: Date | Date[] | (Date | null)[] | null | undefined): void {
  emit('update:customEndDate', normalizeDate(value))
}

const { t } = useI18n()

const periodTypeOptions = computed(() => [
  { label: t('adminDashboard.period.month'), value: 'month' as AdminPeriodType },
  { label: t('adminDashboard.period.quarter'), value: 'quarter' as AdminPeriodType },
  { label: t('adminDashboard.period.year'), value: 'year' as AdminPeriodType },
  { label: t('adminDashboard.period.custom'), value: 'custom' as AdminPeriodType }
])
</script>

<style scoped>
.period-selector {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
}

.period-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.period-dropdown {
  min-width: 220px;
}

.custom-range {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.custom-date {
  width: 10rem;
}

.range-separator {
  color: var(--p-text-muted-color);
}

@media (max-width: 768px) {
  .period-selector {
    flex-direction: column;
    align-items: stretch;
  }

  .period-dropdown {
    width: 100%;
  }

  .custom-range {
    width: 100%;
  }

  .custom-date {
    flex: 1;
    min-width: 0;
  }
}
</style>
